import React, { Component } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Httpconfig from '../helpers/HttpconfigAdmin';
import Constant from '../../constants';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moment from 'moment-timezone';
import { adminPath, getAdminRole, landingForRole } from '../../helpers/adminRoles';
import DateRangeFilter from '../common/DateRangeFilter';

/**
 * Sits between the retailer card grid and the per-request review table.
 * A retailer's pending requests can span many submission batches, so this page
 * groups them by the date they were submitted and lets the admin drill into
 * one date at a time instead of reviewing the whole backlog as one flat list.
 */
class ProductApprovalRetailerDates extends Component {
  constructor(props) {
    super(props);
    this.state = {
      retailer: null,
      dates: [],
      isLoading: true,
      // Draft values bound to the From/To inputs
      dateFrom: '',
      dateTo: '',
      // Values actually applied to the list - only updated when Search is clicked
      appliedFrom: '',
      appliedTo: ''
    };
  }

  componentDidMount() {
    this.fetchRetailerDates();
  }

  fetchRetailerDates = async () => {
    const retailerId = this.props.match.params.retailerId;

    try {
      this.setState({ isLoading: true });

      const response = await Httpconfig.httptokenget(
        `${Constant.siteurl}api/product-approvals/retailer/${retailerId}/dates?status=pending`
      );

      if (response.data.status === 200 && response.data.error === false) {
        this.setState({
          retailer: response.data.data.retailer,
          dates: response.data.data.dates || [],
          isLoading: false
        });
      } else {
        toast.error('Error loading request dates');
        this.setState({ isLoading: false });
      }
    } catch (error) {
      console.error('Error fetching retailer request dates:', error);
      toast.error('Error loading retailer request dates');
      this.setState({ isLoading: false });
    }
  };

  handleViewDate = (date) => {
    const retailerId = this.props.match.params.retailerId;
    this.props.history.push(adminPath(`product-approvals/retailer/${retailerId}/date/${date}`));
  };

  handleViewAll = () => {
    const retailerId = this.props.match.params.retailerId;
    this.props.history.push(adminPath(`product-approvals/retailer/${retailerId}/all`));
  };

  // The inputs hand back YYYY-MM-DD, which is also what the API returns for
  // each submission date, so the drafts are compared as plain strings.
  handleDateFromChange = (event) => this.setState({ dateFrom: event.target.value });
  handleDateToChange = (event) => this.setState({ dateTo: event.target.value });

  handleSearchDateFilter = () => {
    const { dateFrom, dateTo } = this.state;

    if (dateFrom && dateTo && dateFrom > dateTo) {
      toast.error('"From" date cannot be after the "To" date');
      return;
    }

    this.setState({ appliedFrom: dateFrom, appliedTo: dateTo });
  };

  handleClearDateFilter = () =>
    this.setState({ dateFrom: '', dateTo: '', appliedFrom: '', appliedTo: '' });

  getFilteredDates = () => {
    const { dates, appliedFrom, appliedTo } = this.state;

    return dates.filter(entry => {
      if (appliedFrom && entry.date < appliedFrom) return false;
      if (appliedTo && entry.date > appliedTo) return false;
      return true;
    });
  };

  render() {
    const { retailer, dates, isLoading, dateFrom, dateTo, appliedFrom, appliedTo } = this.state;
    const retailerId = this.props.match.params.retailerId;
    const totalPending = dates.reduce((sum, d) => sum + d.pending_count, 0);
    const filteredDates = this.getFilteredDates();
    const isFiltered = Boolean(appliedFrom || appliedTo);
    const canSearch = Boolean(dateFrom || dateTo);
    const canClear = isFiltered || canSearch;

    return (
      <section id="main_dashboard" style={{ overflowX: 'hidden' }}>
        <div className="container" id="main_front">
          <div className="row">
            <div className="col-md-12">
              <div className="dash-section">
                <div className="section-header">
                  <ol className="breadcrumb">
                    <li className="active">
                      <Link to={landingForRole(getAdminRole())}>Dashboard</Link> &gt;{' '}
                      <Link to={adminPath('product-approvals')}>Product Approvals</Link> &gt;{' '}
                      <a>{retailer ? retailer.name : 'Select Date'}</a>
                    </li>
                  </ol>
                </div>
              </div>

              <section id="CMS_tab">
                <div className="CMS_content">
                  <div className="container">
                    <div className="row">
                      <div className="tab-header" style={{ borderBottom: '2px solid #e0e0e0', paddingBottom: '15px', marginBottom: '25px' }}>
                        <h3 style={{ fontSize: '24px', fontWeight: 600, color: '#2c3e50', marginBottom: '5px' }}>Select a Request Date</h3>
                        <p style={{ color: '#7f8c8d', fontSize: '14px', marginBottom: 0 }}>
                          {retailer ? `Choose a submission date to review products from ${retailer.name}` : 'Loading retailer...'}
                        </p>
                      </div>

                      {isLoading ? (
                        <div className="col-md-12 text-center py-5">
                          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                          <p className="mt-3" style={{ fontSize: '15px', color: '#6c757d' }}>Loading request dates...</p>
                        </div>
                      ) : !retailer ? (
                        <div className="col-md-12">
                          <div className="alert alert-danger">Retailer not found</div>
                        </div>
                      ) : (
                        <>
                          {/* Retailer Info Section */}
                          <div className="col-md-12 mb-4">
                            <div style={{
                              backgroundColor: '#d1ecf1',
                              border: '1px solid #bee5eb',
                              borderRadius: '5px',
                              padding: '20px'
                            }}>
                              <div className="row align-items-center">
                                <div className="col-md-9">
                                  <h4 style={{ fontWeight: 700, color: '#0c5460', marginBottom: '12px', fontSize: '20px' }}>
                                    🏪 {retailer.name}
                                  </h4>
                                  <div style={{ fontSize: '14px', color: '#0c5460', marginBottom: '8px' }}>
                                    📍 <strong>Address:</strong> {retailer.address}
                                  </div>
                                  <div className="d-flex gap-4" style={{ fontSize: '14px', color: '#0c5460' }}>
                                    <span><strong>📧 Email:</strong> {retailer.email}</span>
                                    <span><strong>📱 Phone:</strong> {retailer.phone}</span>
                                    <span><strong>🏢 GST:</strong> {retailer.gst_number}</span>
                                  </div>
                                </div>
                                <div className="col-md-3 text-end">
                                  <div style={{
                                    backgroundColor: '#ffc107',
                                    color: '#fff',
                                    padding: '20px',
                                    borderRadius: '10px',
                                    display: 'inline-block',
                                    fontWeight: 700,
                                    marginBottom: '10px'
                                  }}>
                                    <div style={{ fontSize: '28px' }}>{totalPending}</div>
                                    <div style={{ fontSize: '13px' }}>Pending Products</div>
                                  </div>
                                  <div>
                                    <Button
                                      variant="outline-dark"
                                      size="sm"
                                      onClick={this.handleViewAll}
                                      style={{ fontWeight: 500 }}
                                    >
                                      Review All ({totalPending}) →
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-12 mb-3 d-flex justify-content-between align-items-center">
                            <div style={{
                              backgroundColor: '#d4edda',
                              border: '1px solid #c3e6cb',
                              borderRadius: '5px',
                              padding: '12px 20px',
                              display: 'inline-block'
                            }}>
                              <span style={{ color: '#155724', fontWeight: 600 }}>
                                📅 Submission Dates: {isFiltered ? `${filteredDates.length} of ${dates.length}` : dates.length}
                              </span>
                            </div>
                            <Button
                              variant="primary"
                              onClick={this.fetchRetailerDates}
                              disabled={isLoading}
                              style={{ padding: '8px 25px', fontWeight: 500 }}
                            >
                              🔄 Refresh
                            </Button>
                          </div>

                          {/* Date Range Filter - the shared Select Date / Find / Clear
                              bar used by the retailer and diagnostics lists */}
                          <div className="col-md-12 mb-4" id="approval_date_filter">
                            <DateRangeFilter
                              startDate={dateFrom}
                              endDate={dateTo}
                              onStartDateChange={this.handleDateFromChange}
                              onEndDateChange={this.handleDateToChange}
                              onFind={this.handleSearchDateFilter}
                              onClear={this.handleClearDateFilter}
                              canFind={canSearch}
                              canClear={canClear}
                              startId="approval_date_from"
                              endId="approval_date_to"
                            />
                          </div>

                          {dates.length === 0 ? (
                            <div className="col-md-12">
                              <div style={{
                                backgroundColor: '#fff3cd',
                                border: '1px solid #ffc107',
                                borderRadius: '5px',
                                padding: '40px',
                                textAlign: 'center'
                              }}>
                                <div style={{ fontSize: '48px', marginBottom: '15px' }}>📭</div>
                                <h5 style={{ color: '#856404', fontWeight: 600 }}>No pending approval requests</h5>
                              </div>
                            </div>
                          ) : filteredDates.length === 0 ? (
                            <div className="col-md-12">
                              <div style={{
                                backgroundColor: '#fff3cd',
                                border: '1px solid #ffc107',
                                borderRadius: '5px',
                                padding: '40px',
                                textAlign: 'center'
                              }}>
                                <div style={{ fontSize: '48px', marginBottom: '15px' }}>📭</div>
                                <h5 style={{ color: '#856404', fontWeight: 600 }}>No submission dates in this range</h5>
                              </div>
                            </div>
                          ) : (
                            <div className="row">
                              {filteredDates.map((entry) => (
                                <div key={entry.date} className="col-md-6 col-lg-4 mb-4">
                                  <div
                                    onClick={() => this.handleViewDate(entry.date)}
                                    style={{
                                      backgroundColor: 'white',
                                      border: '1px solid #dee2e6',
                                      borderRadius: '8px',
                                      padding: '20px',
                                      transition: 'all 0.2s ease',
                                      cursor: 'pointer',
                                      position: 'relative'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.boxShadow = 'none';
                                      e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                  >
                                    {/* Pending Badge */}
                                    <div style={{
                                      position: 'absolute',
                                      top: '15px',
                                      right: '15px',
                                      backgroundColor: '#ffc107',
                                      color: '#fff',
                                      padding: '6px 12px',
                                      borderRadius: '15px',
                                      fontWeight: 700,
                                      fontSize: '14px'
                                    }}>
                                      {entry.pending_count}
                                    </div>

                                    {/* Date */}
                                    <h5 style={{
                                      fontWeight: 700,
                                      color: '#2c3e50',
                                      marginBottom: '10px',
                                      fontSize: '18px',
                                      paddingRight: '50px'
                                    }}>
                                      📅 {moment(entry.date).format('DD MMM YYYY')}
                                    </h5>

                                    <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '15px' }}>
                                      {moment(entry.date).format('dddd')} · {moment(entry.date).fromNow()}
                                    </p>

                                    {/* Timeline */}
                                    <div style={{ borderTop: '1px solid #e9ecef', paddingTop: '12px' }}>
                                      <div className="row" style={{ fontSize: '12px', color: '#6c757d' }}>
                                        <div className="col-6">
                                          <div style={{ fontWeight: 600, marginBottom: '4px' }}>First Request</div>
                                          <div>{moment(entry.first_request_time).format('hh:mm A')}</div>
                                        </div>
                                        <div className="col-6 text-end">
                                          <div style={{ fontWeight: 600, marginBottom: '4px' }}>Last Request</div>
                                          <div>{moment(entry.last_request_time).format('hh:mm A')}</div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Review Button */}
                                    <Button
                                      variant="success"
                                      className="w-100 mt-3"
                                      style={{ fontWeight: 600, padding: '10px' }}
                                    >
                                      Review Products →
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    );
  }
}

export default ProductApprovalRetailerDates;
